<template>
    <kt-modal 
        :visible="visible" 
        title="Select Contact"
        :size="'modal-md'" 
        @dismiss="$emit('close')"
    >
        <template #body>
            <div v-if="isLoading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
                <p class="mt-2">Loading staff...</p>
            </div>
            <div v-else>
                <div v-if="staffList.length === 0" class="text-center py-4 text-muted">
                    No staff members found for this client.
                </div>
                <div v-else class="list-group">
                    <div
                        v-for="staff in staffList"
                        :key="staff.Id"
                        class="list-group-item d-flex align-items-center gap-2 p-0"
                    >
                        <button
                            type="button"
                            class="btn flex-grow-1 text-start d-flex justify-content-between align-items-center px-3 py-2 border-0"
                            @click="selectStaff(staff)"
                        >
                            <div>
                                <strong>{{ staff.Name || `${staff.FirstName || ''} ${staff.LastName || ''}`.trim() }}</strong>
                                <div class="small text-muted">
                                    <span v-if="staff.Email">{{ staff.Email }}</span>
                                    <span v-if="staff.Email && staff.Phone"> | </span>
                                    <span v-if="staff.Phone">{{ staff.Phone }}</span>
                                </div>
                            </div>
                            <ChevronRight :size="16" class="text-muted" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary me-2"
                            @click="editContact(staff)"
                            title="Edit contact"
                        >
                            <Pencil :size="15" />
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <button @click="openAddContact" class="btn btn-outline-primary me-auto">
                <Plus :size="16" class="me-1" />Add Contact
            </button>
            <button @click="$emit('close')" class="btn btn-outline-secondary">
                <X :size="16" class="me-1" />Cancel
            </button>
        </template>
    </kt-modal>

    <!-- Add Contact Sub-Modal -->
    <kt-modal
        :visible="showContactForm"
        :title="contactFormTitle"
        :size="'modal-md'"
        @dismiss="dismissContactForm"
    >
        <template #body>
            <staff-form v-model="contactForm"></staff-form>
        </template>
        <template #footer>
            <button @click="dismissContactForm" class="btn btn-outline-secondary">
                <X :size="16" class="me-1" />Cancel
            </button>
            <button @click="saveContact" class="btn btn-primary">
                <Save :size="16" class="me-1" />Save
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import Api from '@/services/Api';
import KtModal from '@/components/UI/KtModal.vue';
import StaffForm from '@/components/Staff/StaffForm.vue';
import Alert from '@/services/Alert';
import { X, ChevronRight, Plus, Save, Pencil } from 'lucide-vue-next';

const props = defineProps<{
    visible: boolean;
    clientId?: number | null;
}>();

const emit = defineEmits(['close', 'select']);

const staffList = ref<any[]>([]);
const isLoading = ref(false);
const showContactForm = ref(false);
const contactForm = ref<any>({});
const isEditing = ref(false);
const contactFormTitle = computed(() => isEditing.value ? 'Edit Contact' : 'Add Contact');

const loadStaff = async () => {
    if (!props.clientId) {
        staffList.value = [];
        return;
    }

    isLoading.value = true;
    try {
        const response = await Api.loadStaff({ 
            CustomerNo: [props.clientId],
            Page: 0,
            Size: 999999999
        }, true);
        staffList.value = response ?? [];
    } catch (error) {
        console.error('Error loading staff:', error);
        staffList.value = [];
    } finally {
        isLoading.value = false;
    }
};

const selectStaff = (staff: any) => {
    emit('select', staff);
};

const openAddContact = () => {
    isEditing.value = false;
    contactForm.value = { ClientMap: props.clientId ? [props.clientId] : [] };
    showContactForm.value = true;
};

const editContact = async (staff: any) => {
    try {
        const details = await Api.loadStaffById(staff.Id, true);
        details.ClientMap = (details.ClientMap || []).map((map: any) => map.ClientRef);
        contactForm.value = details;
        isEditing.value = true;
        showContactForm.value = true;
    } catch (error) {
        console.error('Error loading contact:', error);
        Alert.notify({ text: 'Error loading contact', type: 'error' });
    }
};

const dismissContactForm = () => {
    showContactForm.value = false;
    contactForm.value = {};
    isEditing.value = false;
};

const saveContact = async () => {
    try {
        await Api.saveStaff(contactForm.value, true);
        Alert.notify({ text: `Contact ${isEditing.value ? 'updated' : 'created'} successfully!`, type: 'success' });
        dismissContactForm();
        await loadStaff();
    } catch (error) {
        console.error('Error saving contact:', error);
        Alert.notify({ text: 'Error saving contact', type: 'error' });
    }
};

watch(() => props.visible, (newVisible) => {
    if (newVisible && props.clientId) {
        loadStaff();
    }
}, { immediate: true });

onMounted(() => {
    if (props.visible && props.clientId) {
        loadStaff();
    }
});
</script>
